import {
	createLocations,
	deleteLocation,
	getLevelLocations,
	getLocation,
	updateLocation,
} from '@/api/locations';
import { QueryFunctionContext, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { MutationRequest } from '@/api/types';
import { CreateLocationsRequest, UpdateLocationRequest } from '@/api/locations/types';
import { LevelResponse } from '@/api/sites/types';
import { Level } from '@/core/entities';
import { UpdateMutationFnParams, UseMutationQueryParams } from './types';

const locationKeys = {
	allLevelLocations: () => ['all_level_locations'] as const,
	singleLocation: () => ['single_location'] as const,
};

export const useCreateLocations = (mutationQueryParams?: UseMutationQueryParams) =>
	useMutation({
		mutationFn: ({
			id,
			request,
		}: {
			id: string;
			request: MutationRequest<CreateLocationsRequest>;
		}) => createLocations(id, request),
		...mutationQueryParams,
	});

export const useUpdateLocation = (mutationQueryParams?: UseMutationQueryParams) =>
	useMutation({
		mutationFn: ({ id, request }: UpdateMutationFnParams<UpdateLocationRequest>) =>
			updateLocation(id, request),
		...mutationQueryParams,
	});

export const useDeleteLocation = (mutationQueryParams?: UseMutationQueryParams) =>
	useMutation({
		mutationFn: (id: string) => deleteLocation(id),
		...mutationQueryParams,
	});

interface UseLevelLocationsParams {
	queryParams?: {
		limit?: number;
		search?: string;
	};
	level?: Level;
}

export const useLevelLocations = ({ level, queryParams }: UseLevelLocationsParams) => {
	const queryKey = [...locationKeys.allLevelLocations(), level, queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<
			(string | LevelResponse | { search?: string } | undefined)[],
			number
		>) => getLevelLocations(level!.id, { ...queryParams, page: pageParam }),
		enabled: !!level?.id,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
		getNextPageParam: ({ locations: { page, totalPages } }) =>
			page === totalPages ? undefined : page + 1,
	});

	return { queryKey, query };
};

interface UseSingleLocationParams {
	queryParams?: {
		limit?: number;
		search?: string;
	};
	locationId?: string;
}

export const useLocation = ({ locationId, queryParams }: UseSingleLocationParams) => {
	const queryKey = [...locationKeys.singleLocation(), locationId, queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getLocation(locationId!, { ...queryParams, page: pageParam }),
		getNextPageParam: ({ subLocations: { page, totalPages } }) =>
			page === totalPages ? undefined : page + 1,
		enabled: !!locationId,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	return { queryKey, query };
};
